import { useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Lightbulb, CheckCircle2, XCircle, Eye } from "lucide-react";
import { RichText } from "./RichText";
import { Mascot } from "./Mascot";
import { playLessonCorrect, playLessonWrong } from "@/lib/sounds";

export type CheckStatus = "idle" | "correct" | "wrong" | "revealed";

export interface CheckResult {
  correct: boolean; // correct on first try
  attempts: number;
}

export const MAX_ATTEMPTS = 3;

/**
 * State machine shared by every check block:
 *   idle -> (submit) -> correct | wrong -> (retry) ... -> revealed
 * `correct` for scoring means correct on the FIRST attempt.
 */
export function useCheck(onResult: (r: CheckResult) => void, opts?: { maxAttempts?: number; alreadyDone?: CheckResult | null }) {
  const maxAttempts = opts?.maxAttempts ?? MAX_ATTEMPTS;
  const already = opts?.alreadyDone ?? null;
  const [status, setStatus] = useState<CheckStatus>(already ? (already.correct ? "correct" : "revealed") : "idle");
  const [attempts, setAttempts] = useState(already?.attempts ?? 0);
  const [showHint, setShowHint] = useState(false);
  const locked = status === "correct" || status === "revealed";

  const submit = (isCorrect: boolean) => {
    if (locked) return;
    const n = attempts + 1;
    setAttempts(n);
    if (isCorrect) {
      setStatus("correct");
      playLessonCorrect();
      onResult({ correct: n === 1, attempts: n });
      return;
    }
    playLessonWrong();
    if (n >= maxAttempts) {
      setStatus("revealed");
      onResult({ correct: false, attempts: n });
    } else {
      setStatus("wrong");
    }
  };

  const retry = () => {
    if (status === "wrong") setStatus("idle");
  };

  const reveal = () => {
    if (locked) return;
    setStatus("revealed");
    onResult({ correct: false, attempts: Math.max(attempts, 1) });
  };

  return { status, attempts, locked, submit, retry, reveal, showHint, setShowHint, maxAttempts };
}

interface CheckFooterProps {
  check: ReturnType<typeof useCheck>;
  canSubmit: boolean;
  onSubmit: () => void;
  hint?: string;
  feedbackCorrect?: string;
  feedbackWrong?: string;
  explanation?: string;
  /** Rendered when the answer is revealed after too many tries. */
  revealAnswer?: ReactNode;
  checkLabel?: string;
}

const CORRECT_LINES = ["Yes! That is it!", "Wicked! Correct!", "Big brain moment!", "You got it!", "Correct! Keep going!"];
const WRONG_LINES = ["Not quite. Try again!", "Close! Have another go.", "Hmm, take another look."];

export function CheckFooter({ check, canSubmit, onSubmit, hint, feedbackCorrect, feedbackWrong, explanation, revealAnswer, checkLabel = "Check" }: CheckFooterProps) {
  const { status, attempts, maxAttempts, showHint, setShowHint } = check;
  const line = (arr: string[]) => arr[attempts % arr.length];

  return (
    <div className="mt-5 space-y-3">
      <div>
        {status === "correct" && (
          <div key="ok" className="rounded-2xl border-2 border-emerald-400/70 bg-emerald-50 dark:bg-emerald-900/25 p-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="font-bold text-emerald-700 dark:text-emerald-300">
                  {feedbackCorrect ? <RichText text={feedbackCorrect} inline /> : line(CORRECT_LINES)}{attempts === 1 ? " First try!" : ""}
                </p>
                {explanation && <RichText text={explanation} className="text-sm text-emerald-900/80 dark:text-emerald-100/80" />}
              </div>
            </div>
          </div>
        )}
        {status === "wrong" && (
          <div key="wrong" className="rounded-2xl border-2 border-amber-400/70 bg-amber-50 dark:bg-amber-900/25 p-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <div className="flex items-start gap-3">
              <XCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="space-y-1 flex-1">
                <p className="font-bold text-amber-700 dark:text-amber-300">{feedbackWrong ? <RichText text={feedbackWrong} inline /> : line(WRONG_LINES)}</p>
                <p className="text-xs text-amber-800/70 dark:text-amber-100/70">{maxAttempts - attempts} {maxAttempts - attempts === 1 ? "try" : "tries"} left</p>
              </div>
            </div>
          </div>
        )}
        {status === "revealed" && (
          <div key="rev" className="rounded-2xl border-2 border-sky-400/70 bg-sky-50 dark:bg-sky-900/25 p-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <div className="flex items-start gap-3">
              <Eye className="w-6 h-6 text-sky-600 flex-shrink-0 mt-0.5" />
              <div className="space-y-2 flex-1">
                <p className="font-bold text-sky-700 dark:text-sky-300">Here is the answer. You will get it next time!</p>
                {revealAnswer && <div className="text-sm">{revealAnswer}</div>}
                {explanation && <RichText text={explanation} className="text-sm text-sky-900/80 dark:text-sky-100/80" />}
              </div>
            </div>
          </div>
        )}
      </div>

      {showHint && hint && status !== "correct" && status !== "revealed" && (
        <Mascot mood="thinking" message={hint} size="sm" />
      )}

      {(status === "idle" || status === "wrong") && (
        <div className="flex flex-wrap items-center gap-2">
          {status === "idle" ? (
            <Button
              size="lg"
              className="rounded-xl px-8 font-bold text-base"
              disabled={!canSubmit}
              onClick={onSubmit}
              data-testid="button-lesson-check"
            >
              {checkLabel}
            </Button>
          ) : (
            <Button size="lg" className="rounded-xl px-8 font-bold text-base" onClick={check.retry} data-testid="button-lesson-retry">
              Try again
            </Button>
          )}
          {hint && !showHint && (
            <Button variant="outline" className="rounded-xl" onClick={() => setShowHint(true)} data-testid="button-lesson-hint">
              <Lightbulb className="w-4 h-4 mr-1" /> Hint
            </Button>
          )}
          {status === "wrong" && attempts >= 2 && (
            <Button variant="ghost" className="rounded-xl text-muted-foreground" onClick={check.reveal} data-testid="button-lesson-reveal">
              <Eye className="w-4 h-4 mr-1" /> Show me
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

/** Fisher-Yates shuffle that never returns the input order for n >= 2. */
export function shuffled<T>(arr: T[]): T[] {
  if (arr.length < 2) return [...arr];
  let out = [...arr];
  for (let tries = 0; tries < 5; tries++) {
    out = [...arr];
    for (let i = out.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [out[i], out[j]] = [out[j], out[i]];
    }
    if (out.some((v, i) => v !== arr[i])) return out;
  }
  return [out[out.length - 1], ...out.slice(0, -1)];
}
